import { useMemo, useState, useSyncExternalStore } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { sampleBank } from '../core/samples/SampleBank';
import { FX_PRESETS } from '../presets/fxPresets';
import { SYNTH_PRESETS } from '../presets/synthPresets';
import { useAppStore } from '../state/store';

type BrowserTab = 'synth' | 'samples' | 'fx';

/**
 * FL-style browser: synth presets, the sample library (built-in pack +
 * imported files), and FX chain presets. Presets apply to the selected
 * channel of the matching kind.
 */
export function Browser() {
  const [tab, setTab] = useState<BrowserTab>('synth');
  const channel = useAppStore((s) =>
    s.project.channels.find((c) => c.id === s.selectedChannelId),
  );
  const { applySynthPreset, assignSample, applyFxPreset } = useAppStore.getState();
  useSyncExternalStore(sampleBank.subscribe, sampleBank.getVersion);

  const categories = useMemo(
    () => [...new Set(SYNTH_PRESETS.map((p) => p.category))],
    [],
  );
  const samples = sampleBank.list();

  return (
    <div className="browser">
      <div className="browser-tabs">
        <button className={tab === 'synth' ? 'active' : ''} onClick={() => setTab('synth')}>
          Synth
        </button>
        <button className={tab === 'samples' ? 'active' : ''} onClick={() => setTab('samples')}>
          Samples
        </button>
        <button className={tab === 'fx' ? 'active' : ''} onClick={() => setTab('fx')}>
          FX
        </button>
      </div>

      <div className="browser-list">
        {tab === 'synth' && (
          <>
            <div className="browser-hint">
              {channel?.kind === 'synth'
                ? `Loading into ${channel.name}`
                : 'Select a synth channel to load presets'}
            </div>
            {categories.map((cat) => (
              <div key={cat} className="browser-group">
                <div className="browser-cat">{cat}</div>
                {SYNTH_PRESETS.filter((p) => p.category === cat).map((p) => (
                  <button
                    key={p.name}
                    className="browser-item"
                    disabled={channel?.kind !== 'synth'}
                    onClick={() => applySynthPreset(p)}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            ))}
          </>
        )}

        {tab === 'samples' && (
          <>
            <div className="browser-hint">
              {channel?.kind === 'sampler'
                ? `Assigning to ${channel.name}`
                : 'Select a sampler channel to assign samples'}
            </div>
            {samples.map((s) => (
              <div key={s.id} className="browser-item-row">
                <button
                  className="browser-item grow"
                  disabled={channel?.kind !== 'sampler'}
                  onClick={() => assignSample(s.id)}
                  title="Assign to the selected sampler channel"
                >
                  {s.name}
                </button>
                <button
                  className="browser-preview"
                  title="Preview"
                  onClick={() => {
                    void engine.ensureContext().then(() => engine.previewSample(s.id));
                  }}
                >
                  ▶
                </button>
              </div>
            ))}
          </>
        )}

        {tab === 'fx' && (
          <>
            <div className="browser-hint">
              {channel ? `Applying to ${channel.name}` : 'Select a channel to apply FX'}
            </div>
            {FX_PRESETS.map((p) => (
              <button
                key={p.name}
                className="browser-item"
                disabled={!channel}
                onClick={() => applyFxPreset(p.effects)}
              >
                {p.name}
                <span className="browser-sub">{p.effects.map((e) => e.type).join(' + ')}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}